import React from 'react';
import { Button, Typography, Box, Card } from '@mui/material';
import { Link } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';

export const BackButton = ({ to = "/", children = "← Back to Home", ...props }) => {
  const theme = useTheme();

  return (
    <Button
      component={Link}
      to={to}
      variant="text"
      sx={{
        mb: 3,
        color: theme.palette.text.secondary,
        '&:hover': {
          color: theme.palette.text.primary,
          backgroundColor: 'transparent',
        },
      }}
      {...props}
    >
      {children}
    </Button>
  );
};

export const PageHeader = ({ children, color, ...props }) => {
  const theme = useTheme();

  return (
    <Typography
      variant="h3"
      gutterBottom
      sx={{
        color: color || theme.palette.text.primary,
        marginBottom: theme.spacing(3),
        textAlign: 'center',
      }}
      {...props}
    >
      {children}
    </Typography>
  );
};

// Square, unshadowed portrait — no border, no hover scale
export const ProfileImage = styled(Box)(() => ({
  objectFit: 'cover',
  display: 'block',
  margin: '0 auto',
}));

export const HoverCard = styled(Card)(() => ({
  // Flat — inherits MuiCard overrides (no elevation, no hover transform)
}));

export const PageContainer = ({ children, ...props }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        paddingTop: theme.custom.layout.pageTopPadding,
        paddingBottom: theme.spacing(4),
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export const SectionContainer = ({ children, spacing = 'default', ...props }) => {
  const theme = useTheme();

  const spacingValue = spacing === 'large'
    ? theme.custom.layout.sectionSpacing
    : theme.custom.layout.itemSpacing;

  return (
    <Box sx={{ marginBottom: spacingValue }} {...props}>
      {children}
    </Box>
  );
};

export const StyledLogo = ({ src, alt, size = 'medium', filter, ...props }) => {
  const theme = useTheme();
  const logoSize = theme.custom.dimensions.logo[size] || theme.custom.dimensions.logo.medium;

  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        width: logoSize,
        height: logoSize,
        filter: filter,
      }}
      {...props}
    />
  );
};

export const IconContainer = ({ children, size = 'medium', color, ...props }) => {
  const theme = useTheme();
  const iconSize = theme.custom.dimensions.icon[size] || theme.custom.dimensions.icon.medium;

  return (
    <Box
      sx={{
        fontSize: iconSize,
        color: color || theme.palette.text.secondary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

export const MenuCard = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(2.5),
  width: '50%',
  margin: 'auto',
  border: 'none',
}));

export const MenuButton = ({ to, icon, children, color = 'primary', ...props }) => {
  const theme = useTheme();

  return (
    <Button
      variant="outlined"
      sx={{
        margin: theme.spacing(1),
        width: '100%',
        padding: theme.spacing(1.25, 2.5),
        color: theme.palette.text.primary,
        borderColor: theme.palette.divider,
        '&:hover': {
          borderColor: theme.palette.text.primary,
          backgroundColor: 'transparent',
        },
      }}
      component={Link}
      to={to}
      startIcon={icon}
      {...props}
    >
      {children}
    </Button>
  );
};

export const TimelineCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  padding: theme.spacing(2.5),
  // Flat — no hover transform
}));

export const ChipContainer = ({ children, spacing = 1, ...props }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(spacing),
        marginTop: theme.spacing(1),
      }}
      {...props}
    >
      {children}
    </Box>
  );
};
